import { useEffect, useState, useCallback, useId, useRef } from "react";
import { usePortal } from "../PortalShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { focusFirstError } from "@/client/lib/focus-error";
import { Button } from "@/client/components/ui/button";
import { Notice } from "@/client/components/ui/notice";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/client/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPanel,
} from "@/client/components/ui/dialog";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  PlusIcon,
  TrashIcon,
  InfoIcon,
  PencilIcon,
} from "lucide-react";

// ─── Types ───

type Constraint = {
  id: number;
  constraintTypeCode: string;
  constraintEffectCode: string;
  title: string;
  description: string | null;
  appliesToEntireSchool: boolean;
};

type SpecificDate = { constraintId: number; occurrenceDate: string };

// ─── Constants ───

const LATER_TITLE = "__later__";
const NO_TITLE = "__no__";

// All placeholder titles that can appear across the beschikbaarheid data. This section only
// owns the closure (sluitingsdagen) ones, but a school may still carry the per-group / vak
// placeholders from the group dialog, so we keep the full list to exclude them everywhere.
const ALL_PLACEHOLDERS = [
  LATER_TITLE, NO_TITLE,
  "__kleuter_no__", "__kleuter_later__",
  "__groep_no__", "__groep_later__",
  "__vak_no__", "__vak_later__",
];

type Choice = "yes" | "no" | "later" | "";

// ─── Choice derivation ───

/** Sluitingsdagen: closure constraints (school-wide). */
function deriveQ1Choice(constraints: Constraint[]): Choice {
  const items = constraints.filter((c) => c.constraintTypeCode === "closure");
  if (items.some((c) => c.title === LATER_TITLE)) return "later";
  if (items.some((c) => c.title === NO_TITLE)) return "no";
  if (items.some((c) => !ALL_PLACEHOLDERS.includes(c.title))) return "yes";
  return "";
}

// ─── Main component ───

export function VerhinderingenSection({ schoolId }: { schoolId: number }) {
  const { primaryAction, setSaveState, setLastSavedAt, saveState } = usePortal();

  // Seed from the prefetched cache so the first paint is instant (no skeleton); the
  // mount load() still revalidates in the background.
  const cachedConstraints = readPortalCache<{ constraints?: Constraint[]; specificDates?: SpecificDate[] }>(portalQueries.constraints(schoolId).queryKey);

  // Data state
  const [constraints, setConstraints] = useState<Constraint[]>(() => cachedConstraints?.constraints ?? []);
  const [specificDates, setSpecificDates] = useState<SpecificDate[]>(() => cachedConstraints?.specificDates ?? []);
  const [loading, setLoading] = useState(() => !cachedConstraints);
  const [error, setError] = useState(false);

  // Sluitingsdagen answer state.
  const [q1Choice, setQ1Choice] = useState<Choice>(() => (cachedConstraints?.constraints ? deriveQ1Choice(cachedConstraints.constraints) : ""));

  // ─── Data loading ───

  const loadConstraints = useCallback(async () => {
    try {
      const res = await fetch(`/api/school/${schoolId}/constraints`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setConstraints(d.constraints ?? []);
        setSpecificDates(d.specificDates ?? []);
        writePortalCache(portalQueries.constraints(schoolId).queryKey, d);
        return d.constraints ?? [];
      }
    } catch { /* handled by caller */ }
    return null;
  }, [schoolId]);

  const load = useCallback(async () => {
    setError(false);
    const result = await loadConstraints();
    if (!result) {
      setError(true);
      return null;
    }
    return result;
  }, [loadConstraints]);

  useEffect(() => {
    load().then((loaded) => {
      if (loaded) setQ1Choice(deriveQ1Choice(loaded));
      setLoading(false);
    });
  }, [load]);

  const saved = () => {
    setSaveState("saved");
    setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }));
  };

  // ─── API helpers ───

  const apiCreate = async (payload: Record<string, unknown>): Promise<number | null> => {
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/constraints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        await loadConstraints();
        saved();
        return data.id ?? null;
      }
      setSaveState("error");
      return null;
    } catch { setSaveState("error"); return null; }
  };

  const apiUpdate = async (id: number, payload: Record<string, unknown>): Promise<boolean> => {
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/constraints/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) { await loadConstraints(); saved(); return true; }
      setSaveState("error");
      return false;
    } catch { setSaveState("error"); return false; }
  };

  const apiDelete = async (id: number) => {
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/constraints/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) { setSaveState("error"); return; }
      await loadConstraints();
      saved();
    } catch { setSaveState("error"); }
  };

  // ─── Choice handler ───

  // Switching the answer. "Ja" clears only the Nee/later marker and keeps real closure dates;
  // "Nee"/"later" clears EVERY closure item first (so dates entered earlier are actually
  // removed rather than hidden behind the marker), then records the marker. Tracks success
  // so we never flash "Opgeslagen" on a failed call.
  const handleQ1Choice = async (newChoice: Choice) => {
    setQ1Choice(newChoice);
    setSaveState("saving");
    let ok = true;
    const belongs = (c: Constraint) => c.constraintTypeCode === "closure";
    const toRemove = newChoice === "yes"
      ? constraints.filter((c) => belongs(c) && ALL_PLACEHOLDERS.includes(c.title))
      : constraints.filter(belongs);
    for (const c of toRemove) {
      const res = await fetch(`/api/school/${schoolId}/constraints/${c.id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) ok = false;
    }
    if (newChoice === "no" || newChoice === "later") {
      const res = await fetch(`/api/school/${schoolId}/constraints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          constraintTypeCode: "closure",
          constraintEffectCode: "blocked",
          title: newChoice === "no" ? NO_TITLE : LATER_TITLE,
          appliesToEntireSchool: true,
        }),
      });
      if (!res.ok) ok = false;
    }
    await loadConstraints();
    if (ok) saved(); else setSaveState("error");
  };

  // ─── Closure date CRUD ───

  const addClosureDate = async (date: string, title: string) => {
    if (!date || !title.trim()) return false;
    const id = await apiCreate({
      constraintTypeCode: "closure",
      constraintEffectCode: "blocked",
      title: title.trim(),
      appliesToEntireSchool: true,
      specificDates: [{ occurrenceDate: date }],
    });
    return id !== null;
  };

  const updateClosureDate = async (id: number, date: string, title: string) => {
    if (!date || !title.trim()) return false;
    return apiUpdate(id, {
      title: title.trim(),
      specificDates: [{ occurrenceDate: date }],
    });
  };

  // ─── Render ───

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan de studiedagen niet laden"
        action={
          <Button size="sm" variant="outline" onClick={() => {
            setLoading(true);
            load().then((d) => {
              if (d) setQ1Choice(deriveQ1Choice(d));
              setLoading(false);
            });
          }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer uw internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  // ─── Derived data + validity ───

  const q1Closures = constraints.filter(
    (c) => c.constraintTypeCode === "closure" && !ALL_PLACEHOLDERS.includes(c.title)
  );

  // "Ja" needs at least one date; "Nee" and "later" are always confirmable.
  const valid = q1Choice !== "yes" || q1Closures.length > 0;
  const canConfirm = q1Choice !== "" && valid;

  return (
    <div className="space-y-4">
      <ConstraintQuestion
        title="Studiedagen en andere dagen dat de school dicht is"
        description="Zijn er studiedagen of andere losse dagen waarop de hele school dicht is, zoals een teamdag? Vakanties hoeft u niet door te geven."
        choice={q1Choice}
        onChoiceChange={(c) => handleQ1Choice(c as Choice)}
        yesLabel="Ja, dagen toevoegen"
        noLabel="Nee, geen studiedagen"
        laterLabel="Ik vul dit later in"
        showEmptyWarning={q1Choice === "yes" && q1Closures.length === 0}
      >
        <ClosureEditor
          constraints={q1Closures}
          specificDates={specificDates}
          onAdd={addClosureDate}
          onUpdate={updateClosureDate}
          onDelete={apiDelete}
        />
      </ConstraintQuestion>

      {/* ── Confirm bar ── */}
      <div className="flex items-center justify-between gap-4 pt-5">
        <p className="text-xs text-muted-foreground">
          {q1Choice === ""
            ? "Maak een keuze om door te gaan."
            : q1Choice === "later"
              ? "U kunt dit later nog aanvullen."
              : "Uw antwoord wordt automatisch opgeslagen."}
        </p>
        <Button
          size="lg"
          onClick={primaryAction.onClick}
          disabled={primaryAction.isConfirm && (!canConfirm || saveState === "saving")}
        >
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ConstraintQuestion — reusable radio-card question wrapper
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function ConstraintQuestion({
  title, description, helperText, choice, onChoiceChange,
  yesLabel, noLabel, laterLabel, showEmptyWarning, children,
}: {
  title: string;
  description?: string;
  helperText?: string;
  choice: Choice;
  onChoiceChange: (choice: string) => void;
  yesLabel: string;
  noLabel: string;
  laterLabel: string;
  showEmptyWarning: boolean;
  children: React.ReactNode;
}) {
  const baseId = useId();

  // The shell already renders the section title as an H2, so we only keep an sr-only
  // legend for a11y and the question prompt as a calm sub-line above compact radio cards.
  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">{title}</legend>
      {description && <p className="text-sm leading-6 text-muted-foreground">{description}</p>}

      <RadioGroup className="grid gap-2" value={choice} onValueChange={(v) => onChoiceChange(v)}>
        <CompactRadioCard id={`${baseId}-yes`} value="yes" label={yesLabel} selected={choice === "yes"} expanded={choice === "yes"}>
          {helperText && (
            <p className="flex items-start gap-2 rounded-md bg-muted/60 px-2.5 py-2 text-xs leading-5 text-muted-foreground">
              <InfoIcon size={13} className="mt-0.5 shrink-0" />
              {helperText}
            </p>
          )}
          {children}
          {showEmptyWarning && (
            <p className="text-xs leading-5 text-amber-700">
              Voeg minimaal een item toe, of kies een andere optie.
            </p>
          )}
        </CompactRadioCard>

        <CompactRadioCard id={`${baseId}-no`} value="no" label={noLabel} selected={choice === "no"} expanded={false} />

        <CompactRadioCard id={`${baseId}-later`} value="later" label={laterLabel} selected={choice === "later"} expanded={choice === "later"}>
          <p className="text-xs leading-5 text-muted-foreground">
            U kunt dit later nog aanvullen. KUVO ziet dat dit onderdeel nog niet bekend is.
          </p>
        </CompactRadioCard>
      </RadioGroup>
    </fieldset>
  );
}

/** A compact, tactile radio "card": a clickable header row (radio + label, single-fire via
 *  the label) with optional expanding content aligned under the label, not the radio. */
function CompactRadioCard({
  id, value, label, selected, expanded, children,
}: {
  id: string;
  value: string;
  label: string;
  selected: boolean;
  expanded: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`overflow-hidden rounded-lg border shadow-xs motion-safe:transition-colors ${
        selected
          ? "border-primary bg-primary/[0.04] ring-1 ring-primary/20"
          : "border-input hover:border-primary/40 hover:bg-muted/30"
      }`}
    >
      <label htmlFor={id} className="grid cursor-pointer grid-cols-[1.125rem_minmax(0,1fr)] items-center gap-x-3 px-3 py-2.5">
        <RadioGroupItem id={id} value={value} />
        <span className="select-none text-sm font-medium leading-5 text-foreground">{label}</span>
      </label>

      {children && (
        <div
          data-state={expanded ? "expanded" : "collapsed"}
          className="grid motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-200 motion-safe:ease-out data-[state=collapsed]:grid-rows-[0fr] data-[state=collapsed]:opacity-0 data-[state=expanded]:grid-rows-[1fr] data-[state=expanded]:opacity-100"
        >
          <div className="overflow-hidden">
            <div className="grid grid-cols-[1.125rem_minmax(0,1fr)] gap-x-3 px-3 pb-3">
              <div aria-hidden="true" />
              <div className="min-w-0 space-y-3">{children}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ClosureEditor — specific dates with inline editing
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function ClosureEditor({
  constraints, specificDates, onAdd, onUpdate, onDelete,
}: {
  constraints: Constraint[];
  specificDates: SpecificDate[];
  onAdd: (date: string, title: string) => Promise<boolean>;
  onUpdate: (id: number, date: string, title: string) => Promise<boolean>;
  onDelete: (id: number) => Promise<void>;
}) {
  // The add/edit dialog. `editing` holds the closure being edited (null = adding a new one).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: number; title: string; date: string } | null>(null);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c: Constraint) => {
    const date = specificDates.find((sd) => sd.constraintId === c.id)?.occurrenceDate ?? "";
    setEditing({ id: c.id, title: c.title, date });
    setDialogOpen(true);
  };

  // Sort chronologically
  const sorted = [...constraints].sort((a, b) => {
    const dateA = specificDates.find((sd) => sd.constraintId === a.id)?.occurrenceDate ?? "";
    const dateB = specificDates.find((sd) => sd.constraintId === b.id)?.occurrenceDate ?? "";
    return dateA.localeCompare(dateB);
  });

  return (
    <div className="space-y-3">
      {sorted.length > 0 && (
        <div className="space-y-2">
          {sorted.map((c) => {
            const dates = specificDates.filter((sd) => sd.constraintId === c.id);
            const dateStr = dates.map((d) => formatDateWithDay(d.occurrenceDate)).join(", ");
            return (
              <div key={c.id} className="group flex items-center justify-between rounded-md border border-input bg-background px-3 py-2.5">
                <button type="button" onClick={() => openEdit(c)} className="min-w-0 flex-1 cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm">
                  <p className="text-sm font-medium text-foreground">{c.title}</p>
                  {dateStr && <p className="mt-0.5 text-xs text-muted-foreground">{dateStr}</p>}
                </button>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => openEdit(c)} className="cursor-pointer rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100" title="Bewerken">
                    <PencilIcon size={14} />
                  </button>
                  <button onClick={() => onDelete(c.id)} className="cursor-pointer rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-red-600 group-hover:opacity-100" title="Verwijderen">
                    <TrashIcon size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button variant="outline" size="sm" onClick={openAdd}>
        <PlusIcon size={16} className="-ms-0.5 opacity-60" />
        Studiedag toevoegen
      </Button>

      <ClosureDialog
        key={dialogOpen ? (editing ? `edit-${editing.id}` : "new") : "closed"}
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
        onAdd={onAdd}
        onUpdate={onUpdate}
      />
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ClosureDialog — add/edit a single sluitingsdag
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function ClosureDialog({
  open, editing, onClose, onAdd, onUpdate,
}: {
  open: boolean;
  editing: { id: number; title: string; date: string } | null;
  onClose: () => void;
  onAdd: (date: string, title: string) => Promise<boolean>;
  onUpdate: (id: number, date: string, title: string) => Promise<boolean>;
}) {
  // Seeded once at mount; the keyed remount resets per open.
  const [title, setTitle] = useState(() => editing?.title ?? "");
  const [date, setDate] = useState(() => editing?.date ?? "");
  const [dateError, setDateError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const titleId = useId();
  const dateId = useId();

  // The dialog body; on a failed submit we focus + scroll to the first invalid field within it.
  const bodyRef = useRef<HTMLDivElement>(null);

  const valid = !!date && !!title.trim();

  const handleSubmit = async () => {
    const err = validateDate(date);
    if (err) { setDateError(err); focusFirstError(bodyRef.current); return; }
    if (!title.trim()) return;
    setDateError("");
    setSubmitting(true);
    const ok = editing
      ? await onUpdate(editing.id, date, title)
      : await onAdd(date, title);
    setSubmitting(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-w-md" closeProps={{ disabled: submitting }}>
        <DialogHeader>
          <DialogTitle>{editing ? "Studiedag bewerken" : "Studiedag toevoegen"}</DialogTitle>
          <DialogDescription>
            Geef een korte omschrijving en de datum waarop de hele school dicht is.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="space-y-4" ref={bodyRef}>
          <div>
            <Label htmlFor={titleId} className="text-sm font-medium text-foreground">Omschrijving</Label>
            <Input
              id={titleId}
              placeholder="bijv. Studiedag"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor={dateId} className="text-sm font-medium text-foreground">Datum</Label>
            <Input
              id={dateId}
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setDateError(""); }}
              min={SCHOOL_YEAR_START}
              max={SCHOOL_YEAR_END}
              className="mt-1.5"
              aria-invalid={!!dateError}
            />
            {dateError && <p className="mt-1 text-xs text-red-600">{dateError}</p>}
          </div>
        </DialogPanel>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || submitting}>
            {submitting ? "Opslaan..." : editing ? "Wijzigingen opslaan" : "Toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helpers
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Sluitingsdagen moeten binnen het huidige schooljaar vallen (30 augustus 2026 t/m 18 juli 2027).
const SCHOOL_YEAR_START = "2026-08-30";
const SCHOOL_YEAR_END = "2027-07-18";

/** Validate a date string is a real date within the school year. */
function validateDate(date: string): string | null {
  if (!date) return "Vul een datum in.";
  const parsed = new Date(date + "T00:00:00");
  if (isNaN(parsed.getTime())) return "Ongeldige datum.";
  const start = new Date(SCHOOL_YEAR_START + "T00:00:00");
  const end = new Date(SCHOOL_YEAR_END + "T00:00:00");
  if (parsed < start || parsed > end) return "Datum moet binnen het schooljaar vallen (30 augustus 2026 t/m 18 juli 2027).";
  return null;
}

function formatDateWithDay(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("nl-NL", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch { return iso; }
}
